import React, { useContext, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  useToast
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { deliverableApi } from 'services/api/deliverable';
import { getInfluencerPrimaryHandle } from 'utils/influencerUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getClientIdFromCollabRequest } from 'utils/collabUtils';
import UploadFileOrSubmitLinkModal from '../UploadFileOrSubmitLinkModal';
import ReviewContentModalConfirmation from '../ReviewContentModalConfirmation';
import CompletedModal from '../CompletedContentModal';
import ConfirmationModal from '../ConfirmationModal';
import { Campaign } from '@foodfluence/shared';
import { uploadWithProgress } from '../../../../utils/contentUploadUtils';
import mime from 'mime/lite';
import deliverableNames from '../../../../utils/deliverableNames';

const STORY_EXPIRED_CONFIRMED_KEY = 'foodfluence_story_expired_confirmed';

interface StorySubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverableKey: string;
  linkIndex: number;
  showFeedback: boolean;
  rejectionReason: string | null;
  clientName: string;
  clientId: string;
  campaign: Campaign;
  /** Approved draft to post, when the campaign ran a draft-approval round. */
  approvedContentUrl?: string;
}

type ModalState =
  | { type: 'input' }
  | { type: 'validating' }
  | { type: 'preview'; contentUrl: string; contentType: 'image' | 'video'; submissionType: 'link' | 'file'; submissionData: string | File }
  | { type: 'submitting' }
  | { type: 'error'; message: string; errorType: 'validation' | 'submission' }
  | { type: 'success' }
  | { type: 'storyExpiredConfirmation' };

const StorySubmissionModal: React.FC<StorySubmissionModalProps> = ({
  isOpen,
  onClose,
  deliverableKey,
  linkIndex,
  showFeedback,
  rejectionReason,
  clientName,
  clientId,
  campaign,
  approvedContentUrl,
}) => {
  const toast = useToast();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');
  const collabData = useContext(CollabDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const { influencerData } = useContext(InfluencerDataContext);
  const clients = useContext(ClientDataContext);
  const client = clients[getClientIdFromCollabRequest(collab, campaign)];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalState, setModalState] = useState<ModalState>({ type: 'input' });
  const [hasConfirmedStoryExpired, setHasConfirmedStoryExpired] = useState(false);

  useEffect(() => {
    const confirmed = localStorage.getItem(STORY_EXPIRED_CONFIRMED_KEY);
    if (confirmed === 'true') {
      setHasConfirmedStoryExpired(true);
    }
  }, []);

  // This modal stays mounted across story slots, so reset transient state when
  // it closes. Without this, closing mid-preview and reopening for a different
  // slot would show the previous slot's content and could submit it to the new
  // linkIndex. Revoke any file-preview blob URL on the way out.
  useEffect(() => {
    if (isOpen) return;
    if (modalState.type === 'preview' && modalState.submissionType === 'file') {
      URL.revokeObjectURL(modalState.contentUrl);
    }
    setModalState({ type: 'input' });
    setIsSubmitting(false);
    setUploadProgress(0);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- clean up using the state captured at close time

  const modalHeader = `${deliverableNames[deliverableKey as keyof typeof deliverableNames]?.singular} #${linkIndex + 1}`;

  const handleLinkSubmit = async (postLink: string) => {
    if (postLink.trim() === '') {
      toast({
        title: "Link is required.",
        description: "Please enter a valid link before confirming.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await deliverableApi.downloadInstagramStory(postLink, collabId, influencerData?.id);

      setIsSubmitting(false);

      const mimeType = mime.getType(result.storyUrl.split('?')[0]);
      const contentType = mimeType && mimeType.startsWith('video/') ? 'video' : 'image';

      setModalState({
        type: 'preview',
        contentUrl: result.storyUrl,
        contentType,
        submissionType: 'link',
        submissionData: postLink
      });
    } catch (error) {
      console.error("ERROR DOWNLOADING STORY: ", error);
      toast({
        title: "Submission failed.",
        description: "There was an error downloading your story. Please try a different method of submission.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleFileSubmit = async (uploadedFile: File) => {
    if (!uploadedFile) {
      toast({
        title: "File is required.",
        description: "Please upload a file before confirming.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const tempFileUrl = URL.createObjectURL(uploadedFile);
      const contentType = uploadedFile.type.startsWith('video/') ? 'video' : 'image';

      setModalState({
        type: 'preview',
        contentUrl: tempFileUrl,
        contentType: contentType,
        submissionType: 'file',
        submissionData: uploadedFile
      });
    } catch (error) {
      console.error("ERROR UPLOADING FILE: ", error);
      toast({
        title: "Submission failed.",
        description: "There was an error previewing your file. Please try a different method of submission.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleStoryExpiredClick = () => {
    setModalState({ type: 'storyExpiredConfirmation' });
  };

  const handleStoryExpiredConfirm = () => {
    localStorage.setItem(STORY_EXPIRED_CONFIRMED_KEY, 'true');
    setHasConfirmedStoryExpired(true);
    setModalState({ type: 'input' });
  };

  const handleLinkConfirmed = async () => {
    if (modalState.type !== 'preview' || modalState.submissionType !== 'link') {
      return;
    }

    const postLink = modalState.submissionData as string;
    setIsSubmitting(true);

    try {
      await deliverableApi.submitPost({
        collabId: collab.id,
        deliverableKey,
        linkIndex,
        postUrl: postLink,
        influencerHandle: getInfluencerPrimaryHandle(influencerData),
        clientName,
        clientId
      }, influencerData?.id);

      setIsSubmitting(false);
      setModalState({ type: 'success' });
    } catch (error) {
      console.error("ERROR SUBMITTING LINK: ", error);
      setIsSubmitting(false);
      toast({
        title: "Submission failed.",
        description: "There was an error submitting your link.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFileConfirmed = async () => {
    if (modalState.type !== 'preview' || modalState.submissionType !== 'file') {
      return;
    }

    const uploadedFile = modalState.submissionData as File;

    if (!collab) {
      toast({ title: "Error", description: "Collaboration data not found.", status: "error", duration: 3000, isClosable: true });
      return;
    }

    if (!client || !influencerData?.id || !deliverableKey || linkIndex === undefined) {
      toast({ title: "Error", description: "Missing required data for submission.", status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const { url, filePath } = await deliverableApi.getUploadUrl({
        fileName: uploadedFile.name,
        contentType: uploadedFile.type,
        collabId: collab.id,
        clientId: client.id,
        deliverableKey,
        linkIndex,
        submissionType: 'post',
      }, influencerData?.id);

      await uploadWithProgress(url, uploadedFile, (progress) => {
        setUploadProgress(progress);
      });

      await deliverableApi.submitPostAsFile({
        collabId: collab.id,
        deliverableKey,
        linkIndex,
        filePath
      }, influencerData?.id);

      URL.revokeObjectURL(modalState.contentUrl);

      setIsSubmitting(false);
      setModalState({ type: 'success' });
    } catch (error: any) {
      console.error("ERROR SUBMITTING POST: ", error);
      setIsSubmitting(false);
      setUploadProgress(0);

      let errorMessage = "There was an error submitting your post.";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status === 413) {
        errorMessage = "File is too large. Please try a smaller file.";
      } else if (error?.name === "AbortError") {
        errorMessage = "Upload was cancelled. Please try again.";
      } else if (error?.message === "Load failed") {
        errorMessage = "Upload failed. Please check your connection and try again.";
      }

      toast({
        title: "Submission failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmSubmission = async () => {
    if (modalState.type !== 'preview') return;
    if (modalState.submissionType === 'link') {
      await handleLinkConfirmed();
    } else {
      await handleFileConfirmed();
    }
  };

  const handleRetry = () => {
    if (modalState.type === 'preview' && modalState.submissionType === 'file') {
      URL.revokeObjectURL(modalState.contentUrl);
    }
    setModalState({ type: 'input' });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={modalState.type !== 'input'}
      size="md"
      motionPreset={modalState.type === 'input' ? 'none' : 'scale'}
    >
      <ModalOverlay bg="rgba(14,14,14,0.55)" />
      {modalState.type === 'input' && (
        <UploadFileOrSubmitLinkModal
          modalHeader={modalHeader}
          onClose={onClose}
          onSubmitLink={handleLinkSubmit}
          onSubmitFile={handleFileSubmit}
          onStoryExpiredClick={handleStoryExpiredClick}
          showFileUpload={hasConfirmedStoryExpired}
          showFeedback={showFeedback}
          rejectionReason={rejectionReason}
          isSubmitting={isSubmitting}
          uploadProgress={uploadProgress}
          approvedContentUrl={approvedContentUrl}
        />
      )}
      {modalState.type === 'storyExpiredConfirmation' && (
        <ConfirmationModal
          isOpen={isOpen}
          onClose={() => setModalState({ type: 'input' })}
          onConfirm={handleStoryExpiredConfirm}
          title="Story Expired?"
          message="If your story has expired, you can upload the original video or photo from your Instagram story archive or camera roll."
          confirmText="Continue"
          footerWarning={{
            text: "⚠️ No screenshots allowed",
            subtext: "Please download the original video or photo from your Instagram story archive or camera roll"
          }}
        />
      )}
      {modalState.type === 'preview' && (
        <ReviewContentModalConfirmation
          isOpen={isOpen}
          onClose={onClose}
          contentUrl={modalState.contentUrl}
          contentType={modalState.contentType}
          handleSubmitStory={handleConfirmSubmission}
          handleRetry={handleRetry}
          isLoading={isSubmitting}
        />
      )}
      {modalState.type === 'success' && (
        <CompletedModal
          isOpen={isOpen}
          onClose={() => {
            setModalState({ type: 'input' });
            onClose();
          }}
          title="Yay! 🎉"
          subtitle="You submitted your story"
          body="Your content has been successfully submitted to the brand"
        />
      )}
    </Modal>
  );
};

export default StorySubmissionModal;
